export const REFOLLOW_QUEUE_POLL_MS = 60000;
const MAX_BACKOFF_MS = 300000;
const SLOW_THRESHOLD_MS = 5000;
const VISIBILITY_STALE_MS = 30000;

export function refollowQueueRequestKey(scope, page, pageSize, sourceBatchId) {
  const batchPart = sourceBatchId == null ? '' : String(sourceBatchId);
  return `${scope || ''}|${page}|${pageSize}|${batchPart}`;
}

export function nextPollDelayMs(currentMs, baseMs, { success, elapsedMs }) {
  if (success && elapsedMs <= SLOW_THRESHOLD_MS) return baseMs;
  return Math.min(currentMs * 2, MAX_BACKOFF_MS);
}

export function createRefollowQueuePollEngine({
  fetchFn,
  pollMs = REFOLLOW_QUEUE_POLL_MS,
  now = () => Date.now(),
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (id) => clearTimeout(id),
} = {}) {
  const state = {
    fetchFn,
    basePollMs: pollMs,
    nextDelayMs: pollMs,
    now,
    setTimer,
    clearTimer,
    timerId: null,
    abortController: null,
    lastSuccessAt: 0,
    params: null,
    handlers: null,
    stopped: true,
    visible: typeof document === 'undefined' ? true : document.visibilityState === 'visible',
    inFlight: false,
    refetchAfterFlight: null,
  };

  function setHandlers(handlers) {
    state.handlers = handlers;
  }

  function scheduleNext() {
    if (state.stopped || !state.visible) return;
    if (state.timerId != null) state.clearTimer(state.timerId);
    state.timerId = state.setTimer(() => {
      state.timerId = null;
      refetchNow({ reason: 'poll' });
    }, state.nextDelayMs);
  }

  async function refetchNow({ merge = false, silent = false, reason = 'manual' } = {}) {
    if (state.stopped || !state.params || !state.params.enabled) return;
    if (state.inFlight) {
      if (reason !== 'poll') {
        state.refetchAfterFlight = { merge, silent, reason: 'manual' };
      }
      return;
    }
    if (!state.visible && reason === 'poll') return;

    if (state.timerId != null) {
      state.clearTimer(state.timerId);
      state.timerId = null;
    }

    const { scope, page, pageSize, sourceBatchId } = state.params;
    if (!scope || scope === 'draft') return;

    if (state.abortController) state.abortController.abort();
    state.abortController = new AbortController();
    const { signal } = state.abortController;
    const loadReqRef = state.handlers && state.handlers.loadReqRef;
    const reqId = loadReqRef ? ++loadReqRef.current : null;

    const membersLoadedOnceRef = state.handlers && state.handlers.membersLoadedOnceRef;
    const background = silent || (merge && membersLoadedOnceRef && membersLoadedOnceRef.current);
    if (!background && state.handlers && state.handlers.setMemberLoading) {
      state.handlers.setMemberLoading(true);
    }

    state.inFlight = true;
    const started = state.now();
    try {
      const data = await state.fetchFn(
        scope,
        { page, pageSize, sourceBatchId },
        { signal },
      );
      if (signal.aborted) return;
      if (reqId != null && loadReqRef && reqId !== loadReqRef.current) return;
      if (state.handlers && state.handlers.applyData) {
        state.handlers.applyData(data, { merge, silent, background });
      }
      state.lastSuccessAt = state.now();
      state.nextDelayMs = nextPollDelayMs(state.nextDelayMs, state.basePollMs, {
        success: true,
        elapsedMs: state.now() - started,
      });
    } catch (e) {
      if (signal.aborted) return;
      if (reqId != null && loadReqRef && reqId !== loadReqRef.current) return;
      if (!background && state.handlers && state.handlers.onError) state.handlers.onError(e);
      state.nextDelayMs = nextPollDelayMs(state.nextDelayMs, state.basePollMs, {
        success: false,
        elapsedMs: state.now() - started,
      });
    } finally {
      state.inFlight = false;
      if ((reqId == null || (loadReqRef && reqId === loadReqRef.current))
        && !background && state.handlers && state.handlers.setMemberLoading) {
        state.handlers.setMemberLoading(false);
      }
      const pending = state.refetchAfterFlight;
      state.refetchAfterFlight = null;
      if (pending) {
        refetchNow(pending);
        return;
      }
      if (!state.stopped && reason !== 'stop') scheduleNext();
    }
  }

  function start(params, handlers) {
    state.params = params;
    if (handlers) state.handlers = handlers;
    state.stopped = false;
    state.nextDelayMs = state.basePollMs;
    refetchNow({ reason: 'start' });
  }

  function stop() {
    state.stopped = true;
    if (state.timerId != null) {
      state.clearTimer(state.timerId);
      state.timerId = null;
    }
    if (state.abortController) {
      state.abortController.abort();
      state.abortController = null;
    }
  }

  function onVisibilityChange() {
    if (typeof document === 'undefined') return;
    state.visible = document.visibilityState === 'visible';
    if (!state.visible) {
      if (state.timerId != null) {
        state.clearTimer(state.timerId);
        state.timerId = null;
      }
      return;
    }
    const stale = !state.lastSuccessAt || state.now() - state.lastSuccessAt >= VISIBILITY_STALE_MS;
    if (stale && !state.inFlight) refetchNow({ reason: 'visible' });
  }

  return { setHandlers, start, stop, onVisibilityChange, refetchNow, _state: state };
}
